import React, { ReactElement } from 'react';
import _ from 'lodash';
import Link from 'next/link';
import LinesEllipsis from 'react-lines-ellipsis';
import * as plural from 'plural-ru';
import { ITicketsBundles } from "@common/redux/types/ticketsBundles";
import { ISrcSet, Picture } from "@kursk/components/ui/Picture/Picture";
import styles from './TicketsBundlesCard.module.scss';
import moment from 'moment';
import { TicketIcon } from '@kursk/components/ui/icons';
import { getTicketsInfoPriceStr } from '@common/utils/tickets';

interface IProps {
  entity: ITicketsBundles;
}

const checkFree = ({ minPrice, maxPrice }) => maxPrice === minPrice && maxPrice === 0;

export function TicketsBundlesCard({ entity }: IProps): ReactElement {
  const imgSrcSet: ISrcSet[] = [
    {
      media: '(min-width: 768px)',
      width: 290,
      height: 360,
      cropMode: 'noCrop',
    },
    {
      media: '(max-width: 767px)',
      width: 335,
      height: 410,
      cropMode: 'noCrop',
    }
  ];

  const getExpiresIn = () => {
    const end = moment(entity.end);
    const today = moment().endOf('day');
    const tomorrow = moment().add(1, 'day').endOf('day');

    if (end < today) {
      return 'сегодня';
    }
    if (end < tomorrow) {
      return 'завтра';
    }

    const diff = end.diff(moment());
    return diff < 365 ? end.format('D MMMM') : end.format('D MMMM YYYY');
  }

  const getTicketsCount = () => {
    const count = entity.batches.length;
    const ticketsWord = plural.noun(count, 'билета', 'билетов');

    return `Цена абонемента из ${count} ${ticketsWord}`;
  }

  const getPrice = () => {
    const prices = _.pick(entity, 'maxPrice', 'minPrice');
    if (checkFree(prices)) {
      return 'Бесплатно';
    }
    return getTicketsInfoPriceStr(prices);
  }

  const getOriginalPrice = () => getTicketsInfoPriceStr({
    maxPrice: entity.originalPrice,
    minPrice: entity.originalPrice,
  });

  return (
    <Link href={`/ticketsbundles/${entity._id}/${entity.sysName}`}>
      <a>
        <div className={styles.wrapper}>
          <Picture className={styles.image} srcSet={imgSrcSet} image={entity.image}/>
          <div className={styles.info}>
            <div className={styles.badge}>Абонемент</div>
            <div className={styles.ageRestriction}>{entity.ageRestriction || 0}+</div>
            <div className={styles.expiresIn}>Истекает {getExpiresIn()}</div>
            <LinesEllipsis
              text={entity.name}
              maxLine={4}
              ellipsis='...'
              trimRight
              basedOn='letters'
              className={styles.title}
            />
            <LinesEllipsis
              text={entity.description}
              maxLine={3}
              ellipsis='...'
              trimRight
              basedOn='letters'
              className={styles.description}
            />
            <div className={styles.ticketsCount}>{getTicketsCount()}</div>
            <div className={styles.priceContainer}>
              {entity.maxPrice !== 0 && <TicketIcon width={24} height={12}/>}
              <div className={styles.price}>{getPrice()}</div>
              {!!entity.originalPrice && <div className={styles.originalPrice}>{getOriginalPrice()}</div>}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
}
